import { CalendarClock, Globe, Inbox, Wallet, type LucideIcon } from 'lucide-react';

// Mirrors the server's JOB_LISTING_MAX_SCHEDULE_DAYS: how far ahead a listing can be scheduled.
export const JOB_LISTING_MAX_SCHEDULE_DAYS = 90;

// Mirrors the server's JOB_LISTING_DURATION_DAYS / _MIN_ / _MAX_DURATION_DAYS: how long one
// credit keeps a listing online by default, and the range a package or plan can set.
export const JOB_LISTING_DEFAULT_DURATION_DAYS = 30;
export const JOB_LISTING_MIN_DURATION_DAYS = 1;
export const JOB_LISTING_MAX_DURATION_DAYS = 365;

// Mirrors the server's PUBLIC_JOB_LISTINGS_PATH: where published listings are shown publicly.
export const PUBLIC_JOB_LISTINGS_PATH = '/jobs';

// Mirrors the server's PUBLIC_JOB_LISTINGS_API_PATH (under the API base): a live listing as JSON.
export const PUBLIC_JOB_LISTINGS_API_PATH = '/public/job-listings';
// Mirrors the server's PUBLIC_COMPANIES_API_PATH (under the API base): a company's public page as JSON.
export const PUBLIC_COMPANIES_API_PATH = '/public/companies';
// Mirrors the server's PUBLIC_COMPANY_LOGO_API_PATH (under the API base): a company's logo by its public page address.
export const PUBLIC_COMPANY_LOGO_API_PATH = '/public/company-logos';

// Position detail tabs that can be opened from a link (?tab=).
export const POSITION_DETAIL_TAB_PARAM = 'tab';
export const POSITION_DETAIL_TABS = {
  details: 'details',
  jobListing: 'job-listing',
  applications: 'applications',
} as const;
export const positionDetailPath = (positionId: string, tab?: string) =>
  `/positions/${positionId}${tab ? `?${POSITION_DETAIL_TAB_PARAM}=${tab}` : ''}`;

// The listing tab's content form, which "edit listing" scrolls to.
export const JOB_LISTING_CONTENT_CARD_ID = 'job-listing-content';

// Suggestions to the company on its live listing's page, in the order they are offered.
export const OWNER_LISTING_TIPS = { salary: 'salary', benefits: 'benefits', skills: 'skills' } as const;

// The listing page shows this many skills before "+N more".
export const JOB_DETAIL_VISIBLE_SKILLS = 8;

// How long a listing has been up / has left, in days.
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Account settings tabs the job credit screens link to (read from ?tab=).
export const ACCOUNT_SETTINGS_TAB_PARAM = 'tab';
export const ACCOUNT_SETTINGS_JOB_CREDITS_TAB = 'jobCredits';
export const ACCOUNT_SETTINGS_PAYMENT_METHOD_TAB = 'paymentMethod';
export const accountSettingsTabPath = (tab: string) => `/account-settings?${ACCOUNT_SETTINGS_TAB_PARAM}=${tab}`;

// Mirrors the server's JOB_CREDIT_HISTORY_LIMIT: the credit history returns at most this many
// movements, so a full page means an active company (not one that never bought).
export const JOB_CREDIT_HISTORY_LIMIT = 100;

// Where positions are opened and published from.
export const POSITIONS_LIST_PATH = '/positions';

// The job listings introduction shown until a company first buys credits
// (jobCredits.showcase.benefits.<key>.* / jobCredits.showcase.steps.<key>).
export const JOB_LISTING_SHOWCASE_BENEFITS: { key: string; icon: LucideIcon }[] = [
  { key: 'reach', icon: Globe },
  { key: 'applications', icon: Inbox },
  { key: 'schedule', icon: CalendarClock },
  { key: 'flexible', icon: Wallet },
];
export const JOB_LISTING_SHOWCASE_STEPS = ['open', 'publish', 'manage'] as const;

// Currencies a job credit package can be priced in (mirrors the server's Currency enum).
export const JOB_CREDIT_PACKAGE_CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'] as const;

// Mirrors the server's JOB_CREDIT_PACKAGE_MAX_CREDITS / _MAX_VALIDITY_MONTHS.
export const JOB_CREDIT_PACKAGE_MAX_CREDITS = 1000;
export const JOB_CREDIT_PACKAGE_MAX_VALIDITY_MONTHS = 60;

// Job credit plans: billed monthly or yearly (mirrors the server's BillingCycle enum); a yearly
// plan renews its quota every month. Mirrors the server's JOB_CREDIT_PLAN_MAX_MONTHLY_CREDITS.
export const JOB_CREDIT_PLAN_BILLING_CYCLES = ['MONTHLY', 'YEARLY'] as const;
export const JOB_CREDIT_PLAN_MAX_MONTHLY_CREDITS = 1000;
// Mirrors the server's JOB_CREDIT_PLAN_MAX_BUNDLE_DISCOUNT_PERCENT.
export const JOB_CREDIT_PLAN_MAX_BUNDLE_DISCOUNT_PERCENT = 90;
